import './style/main.scss';
import './style/buttons.scss';
import './style/animate.scss';

import CommunicationWrp from './libs/communicationWrp';
import ChatViewport from './chat-viewport/chatViewport';
import {h, Component, JSX, RenderableProps, createContext} from 'preact';
import { IcommunicationObj } from './libs/interfaces';
import {ChatServerMsg, ChatState, HTAction, TYPES} from "./libs/config";
import ChatQueueService from "./chat-viewport/queue-service";
import {Background} from "./components/background";
import {BreadCrumb} from "./components/breadCrumb";
import {ScreenShareButton} from "./components/screenShareButton";
import {Lightbox} from "./lightbox/lightbox";
import {iframeCheck} from "./libs/ScriptHelper";
import {EffectsContext, EffectsContextImpl} from "./effects/effectsContext";
import {VoiceContextImpl, VoiceContext} from "./effects/voiceContext";
import {VoiceButton} from "./components/voiceButton";
import {OfflineDialog} from "./components/offlineDialog";
import {log, LOG_LEVEL} from "./libs/QuerloLogger";
import smoothscroll from 'smoothscroll-polyfill';
import {ConfigItem} from "./chat-msg-elements/set-global-cfg-item/SetGlobalCfgItem";
import {lazy, Suspense} from "preact/compat";
import {Loader} from "./components/loader";

// eslint-disable-next-line no-spaced-func
export const LocalizationContext = createContext<{t: (key: string) => string}>({t: (k: string) => k});
const ThreeDBackground = lazy(() => import('./components/threeDBackground'));
declare const querloChatCfg: any;

export default class App extends Component<{globalConfig: Record<string, any>}, ChatState> {
  private comm: CommunicationWrp;
  private queueService: ChatQueueService;

  private inIframe = false;
  private settableVars: string[] = [];

  private globalCfg: any;
  private chatClass = '';

  private effectContextInstance: EffectsContextImpl;
  private voiceContextInstance: VoiceContextImpl;

  public localizationContext = {
    t: (key: string): string => {
      if (this.globalCfg.trTable[key]) {
        return this.globalCfg.trTable[key];
      }
      return key;
    }
  }

  constructor() {
    super();
    smoothscroll.polyfill();
    log.setLevel(LOG_LEVEL.INFO);
    App.showBanner();
    this.initConfig();
    this.queueService = new ChatQueueService(this.updateChatViewPortCB, this.globalCfg);
    if (typeof this.globalCfg.LOG_LEVEL !== 'undefined') {
      log.setLevel(this.globalCfg.LOG_LEVEL);
    }
    log.exportInstanceToGlobal('QUERLO_CHAT_LOGGER');

    this.comm = new CommunicationWrp(this.onCommunicationChannelEvent, this.globalCfg.chatWSS);
    this.state = {
      messages : [],
      sharerEnabled: false,
      lightBoxChild: null,
      kickstarted: false,
      dynamicStyles: '',
      background: null,
      breadCrumbData: null, //TODO should be an empty array?
      offline: false,
      disconnected: false
    };

    this.effectContextInstance = new EffectsContextImpl();
    this.initTheme(this.globalCfg.THEME);

    this.voiceContextInstance = new VoiceContextImpl(
        this.globalCfg.voice,
        this.globalCfg.staticURL,
        this.globalCfg.adminURL
    );
  }

  componentDidMount(): void {
    this.inIframe = iframeCheck();

    if (this.inIframe) {
      window.addEventListener("message", this.parentEventsListener, false);
      this.sendMessageToParentWindow('chatReady', null);
    }
    
    // adding utility classes to aid in customization based on chat props
    if (this.globalCfg.avatar3d && this.globalCfg.avatar3d.url) {
      this.chatClass += ' has3dAvatar';
    }
    if (this.globalCfg.logo) {
      this.chatClass += ' hasLogo';
    }
    if (this.globalCfg.voice.active) {
      this.chatClass += ' hasVoice';
    }

    this.comm.connect();
  }

  /**
   * handle events received from embed code located in parent window
   * @param e
   */
  private parentEventsListener = (e: any): void => {
    switch (e.data.querloMessageType) {
      case 'CLEAR-CHAT':
        this.queueService.push({t: TYPES.CMD_CLEAR_CHAT, isViewPortMessage: false, id: App.createRandomId(TYPES.CMD_CLEAR_CHAT)} as ChatServerMsg);
        break;
      case 'SET-LOG-LEVEL':
          log.setLevel(e.data.logLevel);
          log.debug(`^^_PARENT_^^ set log level to ${e.data.logLevel}`);
        break;
      case 'GOTO':
        if (e.data.params) { // goto command can contextually set chat variables (TODO should it be refactored and split the 2 actions?)
          this.setServerVariable('params', e.data.params, false)
        }
        this.goto({
          // eslint-disable-next-line @typescript-eslint/camelcase
          chat_id: e.data.chat_id,
          disableAutoBkg: e.data.opts.disableAutoBkg ?? false,
          disableAutoStyle: e.data.opts.disableAutoStyle ?? false,
          disableAutoShareBar: e.data.opts.disableAutoShareBar ?? false
        });
        break;
      case 'SET-CHAT-VARIABLE':
        /** integrity checks */
        if (this.settableVars.length === 0) {
          /** this setVariable received from parent iframe is unexpected (ie it hasn't been queued from java through getClientVariable or it has already been handled,
           * so we don't handle it to prevent unexpected chat behaviors */
          alert('unexpected variable received from parent Iframe');
          return;
        } else if (typeof e.data.varName !== 'undefined' && this.settableVars.indexOf(e.data.varName) === -1) {
          alert('unknown variable received from parent Iframe');
          return;
        }
        // eslint-disable-next-line no-case-declarations
        let serverVarName: string;
        // eslint-disable-next-line no-case-declarations
        let serverVarBody: any;
        // eslint-disable-next-line no-case-declarations
        let debugVarOrigin: string;

        if (typeof e.data.varName === 'undefined') { // fallback for when code in parent iframe doesn't set varName
          serverVarName = this.settableVars.pop() as string;
          serverVarBody = e.data;
          debugVarOrigin = `var name retrieved from last getClientVariable received (LEGACY MODE)`;
        } else {
          serverVarName = this.settableVars.splice(this.settableVars.indexOf(e.data.varName), 1)[0];
          serverVarBody = e.data.varContent;
          debugVarOrigin = `var name retrieved from parent iframe message`;
        }

        log.debug(`^^_PARENT_^^ requested to set server variable (${debugVarOrigin}) '${serverVarName}' to value: `, serverVarBody);
        this.setServerVariable(serverVarName, serverVarBody, true);
        /** tick() shouldn't be needed because server stops sending messages after getClientVariable until flow is resumed through the setServerVariable
         * call, so queue should be empty at this time TODO should we remove it?
         */
        this.queueService.tick();
        break;
      default:
        log.warn(`Unknown command type received from parent i-frame: '${e.data.querloMessageType}', ignoring`);
    }
  }

  private setServerVariable(varName: string, varValue: any, resumeFlow: boolean): void {
    this.dispatchMsg({
      varName,
      value: varValue,
      resumeFlow,
      t: TYPES.SETVARIABLE
    });
  }

  private goto (el: any): void {
    this.dispatchMsg({
      t: TYPES.GOTO,
      b: el
    });
  }

  public updateChatViewPortCB = (messages: ChatServerMsg[]): void => {
    // log.info("setting viewport state to " + JSON.stringify(messages));
    this.setState({messages});
  }

  public payCB = (replyToServerMsg: any): void => {
    this.dispatchMsg(replyToServerMsg);
  }

  public changeBackgroundCB = (bkgData: any) => {
    this.setState({background: bkgData});
  };

  public clearChatCB = () => {
    this.queueService.clearChatMessages();
  };

  public setGlobalCfgItemCallback = (cfgItemData: ConfigItem): void => {
    this.globalCfg[cfgItemData.configKey] = cfgItemData.v;
  }

  public tickCB = (): void => {
    this.queueService.tick();
  }
  
  public lightboxCB = (data: any): void => {
    if (!data) {
      this.setState({ lightBoxChild: null });
      return;
    }
    /**
     *  data.imgURL check is needed to prevent the
     *  screenSharer lightbox to pop into the parent site
     *  TODO find a better way to do it
     */
    if (this.inIframe && data.imgURL) {
      this.sendMessageToParentWindow("clientEvent", {
        name: "OPEN_IMAGE_POPUP",
        params: {
          img: data.imgURL,
          author: data.author,
        },
      });
    } else {
      this.setState({ lightBoxChild: data.node });
    }
  }
  
  public reconnectCB = (): void => {
    window.location.reload(false);
  }

  render(props: RenderableProps<any>, {
    messages,
    sharerEnabled,
    lightBoxChild,
    kickstarted,
    dynamicStyles,
    background,
    breadCrumbData,
    offline,
    disconnected
  }: Readonly<ChatState>): JSX.Element {
    const lastMsgID = messages.length ? messages[messages.length - 1] : '';

    return (
        <LocalizationContext.Provider value={this.localizationContext}>
          <VoiceContext.Provider value={this.voiceContextInstance}>
            <EffectsContext.Provider value={this.effectContextInstance}>

              <Background bkgData={background} assetsBaseURL={this.globalCfg.staticURL} />
              {(this.globalCfg.logo && kickstarted) && <div id="logo">
                <img src={this.globalCfg.logo} alt="branding" />
              </div>}
              {
                this.globalCfg.avatar3d && this.globalCfg.avatar3d.url ? <Suspense fallback={<Loader />}>
                  <ThreeDBackground avatarURL={this.globalCfg.avatar3d.url} />
                </Suspense> : null
              }
              <div className={this.chatClass} role="application">
                <div className="heightWrp">
                  <div id="chatSpawn">
                    {kickstarted ?
                        <ChatViewport messages={messages}
                                      chatID={this.globalCfg.CHAT_ID}
                                      answerCallback={this.answerCB}
                                      payPalId={this.globalCfg.PAYPAL_ID}
                                      voiceInputEnabled={this.globalCfg.voice.active}
                                      changeBackgroundCallback={this.changeBackgroundCB}
                                      clearChatCallback={this.clearChatCB}
                                      backendBaseURL={this.globalCfg.adminURL}
                                      assetsBaseURL={this.globalCfg.staticURL}
                                      payCallback={this.payCB}
                                      lightboxCallback={this.lightboxCB}
                                      tickCallback={this.tickCB}
                                      setGlobalCfgItemCallback={this.setGlobalCfgItemCallback}
                                      isTypingEnabled={this.globalCfg.THEME.show_typing}
                                      messageEnterClass={this.globalCfg.THEME.message_enter_animation}
                        /> :
                      <div className="noMsg">
                        <span>Waiting for incoming messages...</span>
                      </div>}
                  </div>
                </div>
              </div>
              <style id="dynamicstyles">{dynamicStyles}</style>
              {(breadCrumbData && breadCrumbData.length > 0) && (
                  <BreadCrumb breadcrumbData={breadCrumbData} castGotoCB={this.breadCrumbGotoCB} />)}
              <div id="floatButts">
                {this.globalCfg.voice.active && <VoiceButton />}
                {sharerEnabled && (
                    <ScreenShareButton lightboxCallback={this.lightboxCB}
                                       assetsBaseURL={this.globalCfg.staticURL}
                                       backendBaseURL={this.globalCfg.adminURL}
                                       lastMsgID={lastMsgID}
                                       chatID={this.globalCfg.CHAT_ALPHA_ID || this.globalCfg.CHAT_ID} />
                )}
              </div>
              {lightBoxChild && (<Lightbox children={lightBoxChild} lightboxCallback={this.lightboxCB} />)}
              {disconnected && <OfflineDialog offline={offline} reconnectCB={this.reconnectCB} />}

            </EffectsContext.Provider>
          </VoiceContext.Provider>
        </LocalizationContext.Provider>
    );
  }

  public answerCB = (replyToServerMsg: any): void => {
    this.dispatchMsg(replyToServerMsg);
  }

  public breadCrumbGotoCB = (bcData: any): void => {
    const bcDataMod = {...bcData, immediate: true};
    this.queueService.clearQueuedMessages();
    this. dispatchMsg({
      t: TYPES.GOTO,
      b: bcDataMod
    });
  }

  public onCommunicationChannelEvent = (data: IcommunicationObj): void => {

    switch (data.type) {
      case 'onOpen':
        log.debug('CONNECTION: OPEN', data);
        this.setState({disconnected: false});
        this.pushJoinEvent();
        break;
      
      case 'onReconnect':
        log.debug('CONNECTION: RECONNECT');
        this.setState({disconnected: true});
        break;
      
      case 'onReopen':
        log.debug('CONNECTION: REOPEN');
        this.setState({disconnected: false});
        this.pushJoinEvent();
        break;
      
      case 'onMessage':
        this.handleChatServerMessage(data.msg);
        break;
      
      case 'onError':
        log.debug('CONNECTION: ERROR');
        // this.setState({disconnected: true});
        break;
      
      case 'onClose':
        log.debug('CONNECTION: CLOSED');
        // this.setState({disconnected: true});
        break;
      
    }
  };

  private handleCommand(type: string, body: any, chatServerMsg: ChatServerMsg) {
    switch (type) {
      case 'background':
        this.queueService.push({...chatServerMsg, t: TYPES.CMD_CHANGE_BACKGROUND, isViewPortMessage: false, b: body });
        break;
      case 'stickyPrompt':
        this.queueService.push({...chatServerMsg, t: TYPES.CMD_SET_INPUT_UI_STICKY, isViewPortMessage: false, b: body });
        break;
      case 'ht_start':
        this.queueService.push({...chatServerMsg, t: TYPES.HUMAN_TAKEOVER, isViewPortMessage: true, b: {name: body.nick, action: HTAction.HT_START} });
        break;
      case 'ht_end':
        this.queueService.push({...chatServerMsg, t: TYPES.HUMAN_TAKEOVER, isViewPortMessage: true, b: {name: body.nick, action: HTAction.HT_END} });
        break;
      case 'setNick':
        this.queueService.push({...chatServerMsg, t: TYPES.CMD_SET_GLOBAL_CFG_ITEM, isViewPortMessage: false, b: {configKey: `NICK${body.user}`, v: body.v}});
        break;
      case 'setAvatar':
        this.queueService.push({...chatServerMsg, t: TYPES.CMD_SET_GLOBAL_CFG_ITEM, isViewPortMessage: false, b: {configKey: `AVATAR${body.user}`, v: body.v.indexOf("http") === 0 ? body.v : (body.v ? `${this.globalCfg.staticURL}image_uploads/avatar${body.user}/${body.v}` : "")}});
        break;
      case 'enableTimeStamps':
        this.queueService.push({...chatServerMsg, t: TYPES.CMD_SET_GLOBAL_CFG_ITEM, isViewPortMessage: false, b: {configKey: `MSG_TIMESTAMPS`, v: body.v}});
        break;
        // this.setState({background: body});
        // break;
      case 'addStyle':
        //TODO atm it just adds over and over, is it fine?
        //we wait for the first addstyle command to really kickstart the chatbot, to avoid css ghosting and glitches
        if (!this.state.kickstarted) {
          this.setState({ dynamicStyles: body.css, kickstarted: true });
        } else {
          this.setState((prevState) => ({
                dynamicStyles: prevState.dynamicStyles + body.css
              })
          );
        }
        break;
      case 'breadcrumb':
        this.setState({breadCrumbData: body});
        break;
      case 'enableShare':
        this.setState({ sharerEnabled : body });
        break;
        /** this should not be sent as a UI_CHANGE msg as it ends up in the standard msg queue,
         * so we replace the generic TYPES.UI_CHANGE with a dedicate type (existing only on client), to be able
         * to handle it like the other queued messages */
      case 'pay':
        this.queueService.push({...chatServerMsg, t: TYPES.PAY_BUTTON, b: body });
        break;
      case 'clientEvent':
      case 'getClientVariable':
        this.manageClientCommands(type, body);
        break;

      default:
        return;
    }
  }

  private handleChatServerMessage (chatServerMsg: ChatServerMsg): void {
    //this is needed by the youtube player and paypal button
    if (!chatServerMsg.id) {
      chatServerMsg.id = App.createRandomId(chatServerMsg.t);
    }
    chatServerMsg.time = this.globalCfg.MSG_TIMESTAMPS ? App.createTimeString() : "";

    /** used in some switch cases, for counting occurrences **/
    let i = 0;

    //check if this message contains a reload message in case the client is outdated
    //this is then checked again in the onclose callback to call the document.reload()
    switch (chatServerMsg.t) {
      case TYPES.PING_REQUEST:
      // window.reloadingBLChatClient = true;
      this.comm.disconnect();
      return;
      
    case TYPES.RESET_POLL:
      //TODO anything to do here?
      //reset poll request, it's more a request to reset the log of a room! used to show/hide the header
      //log.info('trapped');
            break;
      
    case TYPES.API_CALL:
      
      //TODO this is not supported
      break;
      //reconnect event, will wait a delay after the logout to reconnect
    case TYPES.RECONNECT:
      
      /*this.chat.showAlertAlert(chatServerMsg.b, chatServerMsg.a, chatServerMsg.d * 1000);*/
      
      this.logoutCall();
      setTimeout(() => {
        this.comm.connect();
      }, chatServerMsg.d! * 1000);
      
      return;
      
      //PING REQUEST RECEIVED! LET'S PING BACK!
    case TYPES.PING:
      // this.dispatchMsg({t: TYPES.PING, s:chatServerMsg.s});
      return;

      case TYPES.GENERIC_COMMAND:
        chatServerMsg.b.forEach((cmd: any) => {
          log.debug(`##_CMD_NEW_## ${++i}/${chatServerMsg.b.length} ${cmd.t} received, with data `, cmd.b);
          this.handleCommand(cmd.t, cmd.b, chatServerMsg);
        });
      break;
    case TYPES.UI_CHANGE:
      return;

      default:
      if (chatServerMsg.t === TYPES.SEND_POLL && chatServerMsg.b.questionType === 8 && (!chatServerMsg.b.cards || chatServerMsg.b.cards.length === 0)) {
        /** 'command cards' which trigger chat goto, with no actions to be rendered must not be handled */
        return;
      } else if (chatServerMsg.t === TYPES.USER_TYPING && (!this.globalCfg.THEME.show_typing || this.queueService.isTypingCurrentlyRenderedOrLastScheduled())) {
        /** some themes don't use typing message, so we just skip them. We also avoid showing 2 consecutive typing messages (can happen with INTERRUPTABLE TYPING messages) */
        return;
      }

        /** TEMPORARY PATCH TO FIX RESUMABLE CHATS WHICH ARE NOT RECEIVING STYLES ON BOOT WHEN THEY'RE RESUMED
         * MUST BE FIXED AT JAVA LEVEL AND REMOVED FROM HERE
         */
/*        if (!this.state.kickstarted) {
          this.setState({ kickstarted: true });
        }*/

      this.queueService.push(chatServerMsg);
    }
  }

  static createRandomId(t: number): string {
    return `${t}-${Math.round(new Date().getTime() / 1000)}`;
  }

  private static createTimeString(): string {
    const datetime = new Date(),
      getHour = datetime.getHours(),
      getMinutes = datetime.getMinutes();
    return `${getHour < 10 ? "0" + getHour : getHour}:${
      getMinutes < 10 ? `0${getMinutes}` : getMinutes
    }`;
  }

  private sendMessageToParentWindow(messageType: string, messageBody: any): void {
    window.parent.postMessage({
      querloMessageType: messageType,
      querloMessageBody: messageBody,
      command: messageBody,
    }, '*');
  }

  private manageClientCommands(command: string, commandData: any): void {
    if (command === 'clientEvent' && commandData.name === "Q-INTERRUPTABLE-TYPING") {
      this.handleChatServerMessage({t: TYPES.USER_TYPING, isViewPortMessage: false, id: App.createRandomId(TYPES.USER_TYPING) + "-I", b: {delay: -1}} as ChatServerMsg);
      return;
    }

    if (!this.inIframe) {
      const msg = `##_CMD_## ClientCommand received [${command}], with data: ${JSON.stringify(commandData)} but we're not in iframe, chat is no longer guaranteed to work as expected`;
      alert(msg)
      log.error(msg);

      /** in case we're not in iframe, we would just be waiting forever, so we must clear the requested variable on server (why?)
       * and ask the server to resume flow
       * We also make the queue advance as we won't receive any message from parent iframe to do it (is this really needed?
       * Server should not have sent any further messages after getClientVariable, until client asks it to resume flow through setVariable,
       * so queue should be empty at this point) */
      if (command === 'getClientVariable') {
        this.setServerVariable(commandData.varName, '', true);
        this.queueService.tick(); // TODO should we remove this?
        /** blocking clientEvent when return variable is defined in db */
      } else if (command === 'clientEvent' && commandData.metaData && commandData.metaData.varName) {
        this.setServerVariable(commandData.metaData.varName, '', true);
        this.queueService.tick(); // TODO should we remove this?
      }
      return;
    }

    /** if we got here we're i-framed, then we're good to go */

    if (command === 'getClientVariable') {
      /** add varName to the queue of variables to be set from parent iframe, and just wait for parent iframe
       * to call us back with the variable value so that we can send it to the server and resume chat flow.
       */
      this.settableVars.push(commandData.varName);
      log.debug(`##_CMD_## getClientVariable command received, varName: '${commandData.varName}' queued to be set from parent iframe, variables queued: ${this.settableVars.length}`);
    } else if (command === 'clientEvent' && commandData.metaData && commandData.metaData.varName) {
      this.settableVars.push(commandData.metaData.varName);
      log.debug(`##_CMD_## clientEvent command received with return variable, varName: '${commandData.metaData.varName}' queued to be set from parent iframe, variables queued: ${this.settableVars.length}`);
    }

    /** finally we post the message to the parent of our iframe where it will be handled by embed code */
    this.sendMessageToParentWindow(command, commandData);
  }

  //logout function called from the scope.logout ui button and directly from code
  logoutCall(): void {
    this.comm.disconnect();
    log.info('logout call');
    this.setState({disconnected: true});
  }

//push the register event to the servers, used on connect and reconnect
  pushJoinEvent(): void {
    this.comm.sendMess({
      t: TYPES.REGISTER,
      u: this.globalCfg.user.id,
      n: this.globalCfg.user.id,
      cid: this.globalCfg.CID
    });
    
    /*//auto join the global in case we use it
    this.dispatchMsg({
      t: TYPES.USER_JOINED,
      r: 0
    });*/
  }
  
  dispatchMsg(msgObj: any): void {
    // if (!msgObj.r) msgObj.r = this.chat.selRoom;
    this.comm.sendMess(msgObj);
  }

  private initConfig(): void {
    this.globalCfg = querloChatCfg;
  }

  private initTheme(themeConfig: any): void {
    themeConfig.active_effects.forEach((effectId: number) => {
          this.effectContextInstance.enableEffect(effectId);
        }
    );
  }

  private static showBanner(): void {
    const ban = ["   ____                  __    ",
      '  / __ \\__  _____  _____/ /___ ',
      ' / / / / / / / _ \\/ ___/ / __ \\',
      '/ /_/ / /_/ /  __/ /  / / /_/ /',
      '\\___\\_\\__,_/\\___/_/  /_/\\____/ cHat cliEnt InitialiZed!'].join("\n");
    log.info(ban);
  }
}
